import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './VerbFlowView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages, { getLanguageAcronym } from '../languages'
import { celebrate, popScale } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import SkipNextIcon from '@mui/icons-material/SkipNext'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

const TENSES = [
  { key: 'past', label: 'Past', promptLabel: 'simple past tense' },
  { key: 'present', label: 'Present', promptLabel: 'present tense' },
  { key: 'future', label: 'Future', promptLabel: 'future tense' },
]
const PERSONS = [
  { key: '', label: 'All persons (mixed)' },
  { key: 'I', label: 'I' },
  { key: 'you (singular)', label: 'you (singular)' },
  { key: 'he/she', label: 'he / she' },
  { key: 'we', label: 'we' },
  { key: 'you (plural)', label: 'you (plural)' },
  { key: 'they', label: 'they' },
]

const DEFAULT_COUNT = 10
const STEM_AFTER = 2    // misses before we hand over the (unchanging) stem
const HINT_AFTER = 3    // misses before the text hint + gradual ending reveal
const REVEAL_ALL = 9    // misses before the whole answer is shown

const vocabOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { word: card.source, meaning: t?.target || '', lang: card.sourceLanguage }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { word: t.target, meaning: card.source || '', lang: t.targetLanguage }
  }
  return null
}

const parseItems = (raw) => {
  if (!raw) return []
  let text = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = text.indexOf('{'); const b = text.lastIndexOf('}')
  if (a !== -1 && b !== -1) text = text.slice(a, b + 1)
  try { const o = JSON.parse(text); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}

// Turn a model item into a clean challenge: verb hidden inside its sentence,
// stem/ending guaranteed to reconstruct, and the sentence split around the verb.
const shapeItem = (raw, lang) => {
  const conjugated = String(raw?.conjugated || '').trim()
  if (!conjugated) return null
  let stem = String(raw?.stem || '').trim()
  let ending = String(raw?.ending || '').trim()
  if (!ending || (stem + ending).toLowerCase() !== conjugated.toLowerCase()) {
    if (stem && conjugated.toLowerCase().startsWith(stem.toLowerCase()) && stem.length < conjugated.length) {
      ending = conjugated.slice(stem.length)
    } else { stem = ''; ending = conjugated }
  }
  // Split the sentence around the verb so we can blank only the verb.
  const sentence = String(raw?.sentence || '').trim()
  const masked = String(raw?.sentenceMasked || '').trim()
  let prefix = '', suffix = ''
  const mparts = masked.split(/_{2,}|▁+|＿+/)
  if (masked && mparts.length >= 2) {
    prefix = mparts[0]; suffix = mparts.slice(1).join(' ').trim()
  } else if (sentence) {
    const i = sentence.toLowerCase().indexOf(conjugated.toLowerCase())
    if (i >= 0) { prefix = sentence.slice(0, i); suffix = sentence.slice(i + conjugated.length) }
    else { prefix = sentence + ' '; suffix = '' }
  }
  return {
    verb: String(raw?.verb || '').trim(),
    meaning: String(raw?.meaning || '').trim(),
    tense: String(raw?.tense || '').trim(),
    subjectEnglish: String(raw?.subjectEnglish || '').trim(),
    subjectNative: String(raw?.subjectNative || '').trim(),
    conjugated, stem, ending,
    prefix, suffix,
    sentence,
    english: String(raw?.english || '').trim(),
    hint: String(raw?.hint || '').trim(),
    targetTokens: tokenizeLine(conjugated, lang).map(t => t.norm),
  }
}

// `lang`: pre-pick the language in standalone mode (Special Mode passes the one
// the learner already chose); the picker still lets them change it.
export default function VerbFlowView({ cards, voiceSpeed, onClose, standalone = false, lang }) {
  // Language + class context. Standalone gets a language picker; class mode
  // derives the language and feeds the class vocab to the model as focus.
  const derived = useMemo(() => {
    const v = (cards || []).map(vocabOf).filter(Boolean)
    const counts = {}
    v.forEach(x => { counts[x.lang] = (counts[x.lang] || 0) + 1 })
    const lang = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'eng'
    return { lang, vocab: v.filter(x => x.lang === lang) }
  }, [cards])

  const [manualLang, setManualLang] = useState(lang || 'rus')
  const targetLang = standalone ? manualLang : derived.lang
  const langName = languages[targetLang] || targetLang

  // ── setup ──
  const [query, setQuery] = useState('')
  const [tenses, setTenses] = useState(['present'])   // multi-select
  const [person, setPerson] = useState('')
  const [count, setCount] = useState(DEFAULT_COUNT)
  const [phase, setPhase] = useState('setup') // setup | loading | drill | error
  const [errorMsg, setErrorMsg] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  // ── drill ──
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [hits, setHits] = useState({ forIdx: -1, states: [] })
  const [attempts, setAttempts] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micError, setMicError] = useState(null)
  const [showRoman, setShowRoman] = useState(true)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)

  const item = items[idx]

  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const attemptsRef = useRef(0)
  const lastFinalRef = useRef('')
  const itemRef = useRef(null)
  const comboRef = useRef(null)
  useEffect(() => { itemRef.current = item }, [item])
  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  const toggleTense = (k) => setTenses(prev => prev.includes(k) ? (prev.length > 1 ? prev.filter(x => x !== k) : prev) : [...prev, k])

  // Keep the tense chips in sync with tense words typed in the prompt, so the
  // query and the chips can never disagree ("…past and present" ticks both).
  useEffect(() => {
    const q = query.toLowerCase()
    const found = []
    if (/\bpast\b/.test(q)) found.push('past')
    if (/\bpresent\b/.test(q)) found.push('present')
    if (/\bfuture\b/.test(q)) found.push('future')
    if (found.length) setTenses(found)
  }, [query])

  const canStart = tenses.length > 0 && (standalone ? query.trim().length > 0 : true)

  // ── generation ──────────────────────────────────────────────────────────
  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (isRegen) setRegenerating(true); else setPhase('loading')
    setErrorMsg('')

    const tenseLabels = tenses.map(k => TENSES.find(t => t.key === k)?.promptLabel).filter(Boolean)
    const classVocab = (!standalone && derived.vocab.length)
      ? derived.vocab.slice(0, 40).map(x => `- ${x.word} = ${x.meaning}`).join('\n')
      : ''
    const personRule = person
      ? `Every item uses the subject: ${person}.`
      : `Vary the subject across the set (I, you (singular), he/she, we, you (plural), they).`

    const prompt = `You are a ${langName} teacher creating a SPOKEN verb-conjugation drill.
${query.trim() ? `The learner asked for: "${query.trim()}". Honour this request.` : ''}
${classVocab ? `Prefer verbs the learner is currently studying (pull the verbs out of these):\n${classVocab}` : ''}

Create ${count} drill items. Spread them across these tenses: ${tenseLabels.join(', ')} (roughly even). ${personRule}
Each item drills ONE verb, conjugated for a subject in one of those tenses, inside a short natural sentence.

For each item provide:
- verb: dictionary/infinitive form of the verb
- meaning: short English meaning of the verb (e.g. "to want")
- tense: which tense this item uses — one of ${tenses.join(', ')}
- subjectEnglish: one of "I","you (singular)","he/she","we","you (plural)","they"
- subjectNative: the ${langName} subject pronoun for that person
- conjugated: ONLY the verb, correctly conjugated for that subject & tense (no pronoun)
- stem: the unchanging leading part of "conjugated" (may be empty string)
- ending: the remaining part, so that stem + ending EXACTLY equals conjugated
- sentence: a short natural ${langName} sentence (4-8 words) that uses that exact conjugated verb
- sentenceMasked: the SAME sentence but with the conjugated verb replaced by exactly three underscores ___
- english: the English translation of the sentence
- hint: ONE short English hint about how to form the ending — do NOT state the answer

Return ONLY minified JSON, no markdown:
{"items":[{"verb":"","meaning":"","tense":"","subjectEnglish":"","subjectNative":"","conjugated":"","stem":"","ending":"","sentence":"","sentenceMasked":"","english":"","hint":""}]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ model: 'qwen2.5:14b', prompt, stream: false, format: 'json', options: { temperature: 0.6, num_predict: 2200 } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(r => shapeItem(r, targetLang)).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again or reword the prompt.'); setPhase('error'); setRegenerating(false); return }
      setItems(built)
      setIdx(0)
      setSetNum(n => (isRegen ? n + 1 : 1))
      setRegenerating(false)
      setPhase('drill')
    } catch (e) {
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Generation failed.'))
      setPhase('error'); setRegenerating(false)
    }
  }, [serverUrl, apiKey, standalone, derived.vocab, person, tenses, count, query, langName, targetLang])

  // Reset per item.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setHits({ forIdx: idx, states: item.targetTokens.map(() => false) })
    setAttempts(0); setRevealed(false); setInterim('')
    completedRef.current = false; attemptsRef.current = 0; lastFinalRef.current = ''
  }, [idx, phase, item])

  const states = hits.forIdx === idx ? hits.states : []

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + 1
      if (next >= items.length) { generate(true); return prev }   // endless: build next set
      return next
    })
  }, [items.length, generate])

  const skip = () => { setCombo(0); advance() }

  // Verb recalled → combo, celebrate, fast next.
  useEffect(() => {
    if (hits.forIdx !== idx || !item) return
    if (item.targetTokens.length === 0 || hits.states.length !== item.targetTokens.length) return
    if (!hits.states.every(Boolean) || completedRef.current) return
    completedRef.current = true
    celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 26, spread: 160 })
    setCombo(c => {
      const nc = c + 1
      setBest(b => Math.max(b, nc))
      if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 60, spread: 260 })
      return nc
    })
    advanceTimerRef.current = setTimeout(advance, 850)
  }, [hits, idx, item, advance])

  const applyTranscript = useCallback((transcript, isFinal) => {
    const cur = itemRef.current
    if (!cur) return
    const tokens = tokenizeTranscript(transcript, targetLang)
    setHits(prev => {
      if (prev.forIdx !== idx) return prev
      const next = [...prev.states]
      for (let ti = 0; ti < cur.targetTokens.length; ti++) {
        if (next[ti]) continue
        if (tokens.some(tok => tokenMatches(tok, cur.targetTokens[ti], targetLang))) next[ti] = true
      }
      return { forIdx: prev.forIdx, states: next }
    })
    if (isFinal && !completedRef.current) {
      const heard = transcript.trim()
      if (heard && heard !== lastFinalRef.current) {
        lastFinalRef.current = heard
        attemptsRef.current += 1
        setAttempts(attemptsRef.current)
        if (attemptsRef.current >= REVEAL_ALL) setRevealed(true)
      }
    }
  }, [idx, targetLang])

  // ── recognizer ────────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    listeningRef.current = false; setListening(false); sessionRef.current++
    try { recRef.current?.stop() } catch { /* stopped */ }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) { setMicError('Speech recognition is not supported in this browser.'); return }
    if (!itemRef.current) return
    setMicError(null); cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[targetLang] || 'en-US'
    rec.continuous = true; rec.interimResults = true
    let heardSound = false
    const stopWithError = (msg) => { listeningRef.current = false; setListening(false); setMicError(msg) }
    rec.onsoundstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onspeechstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true; deadSessionsRef.current = 0
      let transcript = '', sawFinal = false
      for (let i = 0; i < event.results.length; i++) { transcript += event.results[i][0].transcript + ' '; if (event.results[i].isFinal) sawFinal = true }
      setInterim(transcript.trim().slice(-70))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current || completedRef.current) return
      if (!heardSound) { deadSessionsRef.current++; if (deadSessionsRef.current >= 4) { stopWithError('Mic is open but no sound is reaching it — check the input in the address-bar site settings.'); return } }
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* raced */ } } }, 500)
    }
    recRef.current = rec; listeningRef.current = true; deadSessionsRef.current = 0; setListening(true)
    try { rec.start() } catch { setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* give up */ } } }, 400) }
  }, [targetLang, applyTranscript])

  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 300)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    listeningRef.current = false; sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  const retry = () => {
    clearTimeout(advanceTimerRef.current); completedRef.current = false
    setHits({ forIdx: idx, states: (item?.targetTokens || []).map(() => false) }); setInterim('')
    if (listeningRef.current) { sessionRef.current++; try { recRef.current?.abort() } catch { /* fine */ }; setTimeout(startListening, 250) }
  }
  const speakAnswer = () => { if (item) speak(item.sentence || item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }
  const speakConjugated = () => { if (item) speak(item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }

  // ── render: setup ──────────────────────────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Verb Lab{standalone ? '' : ` · ${langName}`}</div>
          <h1 className={styles.title}>Conjugation practice</h1>
          <p className={styles.sub}>
            {standalone ? 'Say what you want to drill — the AI builds it.' : `${derived.vocab.length} items from this class · say the conjugated verb out loud.`}
          </p>

          {phase === 'loading' ? (
            <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>Building {count} conjugation challenges…</div></div>
          ) : (
            <>
              {standalone && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Language</span>
                  <select className={styles.select} value={manualLang} onChange={e => setManualLang(e.target.value)}>
                    {Object.entries(languages).filter(([c]) => c !== 'eng').map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </div>
              )}

              <div className={styles.field}>
                <span className={styles.fieldLabel}>What do you want to practise?{standalone ? '' : ' (optional)'}</span>
                <textarea
                  className={styles.queryBox}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={standalone
                    ? `e.g. "the most common Russian verbs in present & past"\nor "ser and estar in the past tense in Spanish"`
                    : 'Leave blank to drill this class’s verbs, or focus it — e.g. "just past tense", "focus on идти and хотеть"'}
                  rows={2}
                />
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Tense (pick one or more)</span>
                <div className={styles.chipRow}>
                  {TENSES.map(t => (
                    <button key={t.key} className={`${styles.chip} ${tenses.includes(t.key) ? styles.chipActive : ''}`} onClick={() => toggleTense(t.key)}>{t.label}</button>
                  ))}
                </div>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Person</span>
                <select className={styles.select} value={person} onChange={e => setPerson(e.target.value)}>
                  {PERSONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>How many per set</span>
                <div className={styles.chipRow}>
                  {[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}
                </div>
              </div>

              {phase === 'error' && <div className={styles.error}>{errorMsg}</div>}
              <button className={styles.generateBtn} onClick={() => generate(false)} disabled={!canStart}>🎤 Start drill</button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── render: drill ───────────────────────────────────────────────────────────
  const roman = romanizationOf(item?.sentence, targetLang)
  const showHint = attempts >= HINT_AFTER && item?.hint
  const recLangLabel = recognitionLangs[targetLang] || 'en-US'
  const got = states.length > 0 && states.every(Boolean)
  const showStem = attempts >= STEM_AFTER || revealed
  const revealCount = revealed ? (item?.ending || '').length : Math.max(0, Math.min((item?.ending || '').length, attempts - HINT_AFTER))
  const itemTenseLabel = TENSES.find(t => t.key === item?.tense)?.label || TENSES.find(t => tenses.includes(t.key))?.label

  const Blank = () => {
    if (got) return <span className={styles.answerGot}>{item.conjugated}</span>
    if (!showStem) return <span className={styles.blankInline}>{'_'.repeat(Math.max(3, (item?.conjugated || '').length))}</span>
    const shownEnd = (item?.ending || '').slice(0, revealCount)
    const hiddenLen = Math.max(0, (item?.ending || '').length - revealCount)
    return (
      <span className={styles.answerInline}>
        <span className={styles.stem}>{item.stem}</span>
        <span className={styles.ending}>{shownEnd}</span>
        {hiddenLen > 0 && <span className={styles.blankInline}>{'_'.repeat(Math.max(1, hiddenLen))}</span>}
      </span>
    )
  }

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={() => setPhase('setup')}><CloseIcon /></button>
      {regenerating && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · {itemTenseLabel} · {languages[targetLang] || targetLang}</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '⚡'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx}>
          <div className={styles.subjectRow}>
            <span className={styles.subjectChip}>{item?.subjectEnglish || '—'}</span>
            {item?.subjectNative && <span className={styles.subjectNative}>{item.subjectNative}</span>}
            {item?.tense && <span className={styles.tenseTag}>{itemTenseLabel}</span>}
          </div>

          <div className={styles.verbInfo}>conjugate <strong>{item?.verb}</strong>{item?.meaning ? ` — ${item.meaning}` : ''}</div>

          {/* the sentence, with ONLY the verb blanked */}
          <div className={styles.sentenceLine}>
            <span className={styles.ctx}>{item?.prefix}</span>
            <Blank />
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>

          {showRoman && roman && <div className={styles.roman}>{roman}</div>}
          {item?.english && <div className={styles.english}>{item.english}</div>}

          {showHint && <div className={styles.hintBox}>💡 {item.hint}</div>}
          {revealed && !got && <div className={styles.revealNote}>Answer: <strong>{item?.conjugated}</strong> — say it or skip ahead.</div>}
        </div>

        <div className={styles.interim}>
          {micError ? micError : listening ? (interim || `mic open (${recLangLabel}) — say the verb`) : (attempts > 0 ? `${attempts} attempt${attempts === 1 ? '' : 's'}` : 'tap the mic and say the conjugated verb')}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={speakConjugated} title="Hear the verb"><VolumeUpIcon /></button>
          <button className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`} onClick={() => (listening ? stopListening() : startListening())} title={listening ? 'Stop the mic' : 'Speak the conjugation'}>
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={retry} title="Retry"><ReplayIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>

        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={speakAnswer}>hear full sentence</button>
          {!revealed && <button className={styles.linkBtn} onClick={() => setRevealed(true)}>reveal answer</button>}
          <label className={styles.toggle}><input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} /> romanization</label>
        </div>
      </div>
    </div>
  )
}
